/**
 * Profile Page for trAInedup.ai
 * User profile management and settings interface
 */

import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  User,
  Mail,
  MapPin,
  Briefcase,
  Edit3,
  Save,
  X,
  Camera,
  Shield,
  Award,
  CheckCircle,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Card } from '../components/common/Card';
import { Button, ButtonLink } from '../components/common/Button';
import { Input } from '../components/common/Input';
import { Layout } from '../components/common/Layout';
import useGamification from '../hooks/useGamification';
import { useDashboardStats } from '../hooks/useDashboardStats';
import { DEFAULT_TASKS, GamificationTask } from '../types/gamification';

/**
 * Profile page component
 * Allows users to view and edit their profile information
 */
export const ProfilePage: React.FC = () => {
  const { t } = useTranslation('pages/profile');
  const { user, updateProfile, updateEmailAddress, reauthenticate } = useAuth();
  const { progress, loading: gamificationLoading } = useGamification();
  const { stats: dashboardStats } = useDashboardStats();
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showReauthModal, setShowReauthModal] = useState(false);
  const [password, setPassword] = useState('');
  const [reauthError, setReauthError] = useState('');
  
  const [formData, setFormData] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    displayName: user?.displayName || '',
    email: user?.email || '',
    bio: user?.bio || '',
    location: user?.location || '',
    jobTitle: user?.jobTitle || '',
    company: user?.company || '',
    website: user?.website || '',
    linkedIn: user?.linkedIn || '',
    twitter: user?.twitter || '',
    youtube: user?.youtube || '',
  });

  // Update form data when user changes
  React.useEffect(() => {
    if (user) {
      setFormData({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        displayName: user.displayName || '',
        email: user.email || '',
        bio: user.bio || '',
        location: user.location || '',
        jobTitle: user.jobTitle || '',
        company: user.company || '',
        website: user.website || '',
        linkedIn: user.linkedIn || '',
        twitter: user.twitter || '',
        youtube: user.youtube || '',
      });
    }
  }, [user]);

  const latestCompletedTasks = useMemo(() => {
    if (!progress?.completedTasks?.length) {
      return [] as GamificationTask[];
    }

    const recentTaskIds = progress.completedTasks.slice(-3).reverse();

    return recentTaskIds
      .map(taskId => DEFAULT_TASKS.find(task => task.id === taskId))
      .filter((task): task is GamificationTask => Boolean(task));
  }, [progress?.completedTasks]);

  /**
   * Handle form input changes
   */
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  /**
   * Handle re-authentication and save
   */
  const handleReauthAndSave = async () => {
    if (!password) {
      setReauthError('Password is required');
      return;
    }

    setIsLoading(true);
    setReauthError('');

    try {
      const reauthResult = await reauthenticate(password);
      if (!reauthResult.success) {
        setReauthError(reauthResult.error || 'Incorrect password');
        setIsLoading(false);
        return;
      }

      // Close modal and proceed with save
      setShowReauthModal(false);
      setPassword('');
      await performSave();
    } catch (error: any) {
      setReauthError(error.message || 'Authentication failed');
      setIsLoading(false);
    }
  };

  /**
   * Perform the actual save operation
   */
  const performSave = async () => {
    if (!user?.uid) return;
    
    setIsLoading(true);
    setError('');

    try {
      // Auto-generate displayName from firstName and lastName if not provided
      const updatedFormData = {
        ...formData,
        displayName: formData.displayName || `${formData.firstName} ${formData.lastName}`.trim(),
      };
      
      // Check if email changed
      let emailChangeMessage = '';
      if (formData.email !== user.email) {
        const emailResult = await updateEmailAddress(formData.email);
        if (emailResult.success) {
          emailChangeMessage = ` A verification email has been sent to ${formData.email}. Please verify it to update your email address.`;
        } else {
          // If we hit a security error despite re-auth (shouldn't happen often), handle it
          if (emailResult.errorCode === 'auth/requires-recent-login') {
             // This path should be covered by the re-auth flow, but just in case
             setShowReauthModal(true);
             setIsLoading(false);
             return;
          }
          throw new Error(emailResult.error || 'Failed to initiate email update');
        }
      }

      // Remove email from updates sent to Firestore to prevent premature update
      const { email, ...firestoreUpdates } = updatedFormData;
      // Log to avoid unused variable lint error and for debugging
      // eslint-disable-next-line no-console
      console.log('Updating profile, excluding email from direct update:', email);

      const result = await updateProfile(user.uid, firestoreUpdates);
      
      if (result.success) {
        setIsEditing(false);
        if (emailChangeMessage) {
          alert('Profile updated.' + emailChangeMessage);
        }
      } else {
        setError(result.error || 'Failed to update profile');
      }
    } catch (error: any) {
      setError(error.message || 'Failed to update profile');
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Handle profile update
   */
  const handleSave = async () => {
    // If email is changing, require re-authentication first
    if (formData.email !== user?.email) {
      setShowReauthModal(true);
      return;
    }

    await performSave();
  };

  /**
   * Cancel editing and reset form
   */
  const handleCancel = () => {
    setFormData({
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      displayName: user?.displayName || '',
      email: user?.email || '',
      bio: user?.bio || '',
      location: user?.location || '',
      jobTitle: user?.jobTitle || '',
      company: user?.company || '',
      website: user?.website || '',
      linkedIn: user?.linkedIn || '',
      twitter: user?.twitter || '',
      youtube: user?.youtube || '',
    });
    setIsEditing(false);
    setError('');
  };

  return (
    <Layout showHeader showFooter>
      <div className="p-6 max-w-4xl mx-auto space-y-8">
      {/* Profile Header */}
      <Card>
        <div className="flex flex-col md:flex-row items-start md:items-center space-y-4 md:space-y-0 md:space-x-6">
          {/* Profile Picture */}
          <div className="relative">
            <div className="w-24 h-24 bg-primary rounded-full flex items-center justify-center">
              {user?.photoURL ? (
                <img
                  src={user.photoURL}
                  alt="Profile"
                  className="w-24 h-24 rounded-full object-cover"
                />
              ) : (
                <User className="w-12 h-12 text-white" />
              )}
            </div>
            {isEditing && (
              <button className="absolute bottom-0 right-0 w-8 h-8 bg-primary rounded-full flex items-center justify-center text-white hover:bg-primary-600 transition-colors duration-200">
                <Camera className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Profile Info */}
          <div className="flex-1">
            <div className="flex flex-col md:flex-row md:items-center justify-between mb-4 gap-4">
              <div>
                <h1 className="font-montserrat font-bold text-2xl text-neutral-800">
                  {user?.displayName || t('user')}
                </h1>
                <p className="text-neutral-600">{user?.email}</p>
                {user?.jobTitle && (
                  <p className="text-sm text-neutral-500">
                    {user.jobTitle} {user.company && `${t('at')} ${user.company}`}
                  </p>
                )}
              </div>
              
              {!isEditing ? (
                <Button
                  variant="outline"
                  onClick={() => setIsEditing(true)}
                  icon={Edit3}
                >
                  {t('editProfile')}
                </Button>
              ) : (
                <div className="flex space-x-2">
                  <Button
                    variant="primary"
                    onClick={handleSave}
                    loading={isLoading}
                    icon={Save}
                  >
                    {t('save')}
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={handleCancel}
                    disabled={isLoading}
                    icon={X}
                  >
                    {t('cancel')}
                  </Button>
                </div>
              )}
            </div>

            {/* User Role Badge */}
            {/*
              NOTE: Always use the English value from storage (e.g., user.roles[0], user.subscriptionTier) for logic, storage, and API/database calls.
              Only translate for display in the UI. Do NOT store or use translated values in the backend or queries.
            */}
            <div className="flex items-center space-x-2 mb-4">
              <div className="flex items-center space-x-1 px-3 py-1 bg-primary-100 text-primary rounded-full text-sm">
                <Shield className="w-4 h-4" />
                <span className="capitalize">{user?.roles?.[0] ? t(`roles.${user.roles[0].toLowerCase()}`) : t('student')}</span>
              </div>
              
              {user?.subscriptionTier && (
                <div className="flex items-center space-x-1 px-3 py-1 bg-secondary-100 text-secondary rounded-full text-sm">
                  <Award className="w-4 h-4" />
                  <span className="capitalize">{t(`subscriptionTiers.${user.subscriptionTier.toLowerCase()}`)}</span>
                </div>
              )}
            </div>

            {/* Bio */}
            {(user?.bio || isEditing) && (
              <p className="text-neutral-600">
                {user?.bio || t('noBio')}
              </p>
            )}
          </div>
        </div>
      </Card>

      {/* Re-authentication Modal */}
      {showReauthModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 animate-fade-in">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Confirm Password</h3>
            <p className="text-gray-600 mb-6">
              For your security, please enter your password to confirm the email change.
            </p>
            
            <div className="space-y-4">
              <Input
                label="Password"
                name="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                error={reauthError}
              />
              
              <div className="flex space-x-3 justify-end mt-6">
                <Button
                  variant="ghost"
                  onClick={() => {
                    setShowReauthModal(false);
                    setPassword('');
                    setReauthError('');
                  }}
                  disabled={isLoading}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={handleReauthAndSave}
                  loading={isLoading}
                >
                  Confirm & Save
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Security Logout Requirement (Fallback) - Removed */}

      {/* Error Message */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-600 text-sm">{error}</p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Profile Details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Personal Information */}
          <Card>
            <h2 className="font-montserrat font-semibold text-xl text-neutral-800 mb-6">
              {t('personalInfo')}
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Input
                label={t('firstName')}
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
                disabled={!isEditing}
                icon={User}
                placeholder={t('firstNamePlaceholder')}
              />

              <Input
                label={t('lastName')}
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
                disabled={!isEditing}
                icon={User}
                placeholder={t('lastNamePlaceholder')}
              />

              <Input
                label={t('displayName')}
                name="displayName"
                value={formData.displayName}
                onChange={handleInputChange}
                disabled={!isEditing}
                icon={User}
                placeholder={t('displayNamePlaceholder')}
              />

              <Input
                label={t('emailAddress')}
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                disabled={!isEditing}
                icon={Mail}
              />

              <Input
                label={t('location')}
                name="location"
                value={formData.location}
                onChange={handleInputChange}
                disabled={!isEditing}
                icon={MapPin}
                placeholder={t('locationPlaceholder')}
              />

              <Input
                label={t('jobTitle')}
                name="jobTitle"
                value={formData.jobTitle}
                onChange={handleInputChange}
                disabled={!isEditing}
                icon={Briefcase}
                placeholder={t('jobTitlePlaceholder')}
              />

              <Input
                label={t('company')}
                name="company"
                value={formData.company}
                onChange={handleInputChange}
                disabled={!isEditing}
                placeholder={t('companyPlaceholder')}
              />

              <Input
                label={t('website')}
                name="website"
                value={formData.website}
                onChange={handleInputChange}
                disabled={!isEditing}
                placeholder={t('websitePlaceholder')}
              />
            </div>

            {/* Bio */}
            <div className="mt-6">
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                {t('bio')}
              </label>
              <textarea
                name="bio"
                value={formData.bio}
                onChange={handleInputChange}
                disabled={!isEditing}
                rows={4}
                className="w-full px-4 py-3 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors duration-200 disabled:bg-neutral-50 disabled:text-neutral-500"
                placeholder={t('bioPlaceholder')}
              />
            </div>
          </Card>

          {/* Social Links */}
          <Card>
            <h2 className="font-montserrat font-semibold text-xl text-neutral-800 mb-6">
              {t('socialLinks')}
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Input
                label={t('linkedIn')}
                name="linkedIn"
                value={formData.linkedIn}
                onChange={handleInputChange}
                disabled={!isEditing}
                placeholder={t('linkedInPlaceholder')}
              />

              <Input
                label={t('twitter')}
                name="twitter"
                value={formData.twitter}
                onChange={handleInputChange}
                disabled={!isEditing}
                placeholder={t('twitterPlaceholder')}
              />

              <Input
                label="YouTube"
                name="youtube"
                value={formData.youtube}
                onChange={handleInputChange}
                disabled={!isEditing}
                placeholder="https://www.youtube.com/@yourhandle"
              />
            </div>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Learning Stats */}
          <Card>
            <h3 className="font-montserrat font-semibold text-lg text-neutral-800 mb-4">
              {t('learningStats')}
            </h3>

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-neutral-600">{t('coursesCompleted')}</span>
                <span className="font-semibold text-neutral-800">{dashboardStats?.coursesCompleted || 0}</span>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-neutral-600">{t('learningTime')}</span>
                <span className="font-semibold text-neutral-800">{(progress?.totalXP || 0).toLocaleString()}</span>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-neutral-600">{t('currentStreak')}</span>
                <span className="font-semibold text-neutral-800">{dashboardStats?.currentStreak || 0} days</span>
              </div>
            </div>
          </Card>

          {/* AI Readiness Tasks */}
          <Card>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-montserrat font-semibold text-lg text-neutral-800">
                {t('aiReadiness')}
              </h3>
              {typeof progress?.readinessPercentage === 'number' && (
                <span className="text-sm font-medium text-neutral-600">
                  {progress.readinessPercentage.toFixed(1)}%
                </span>
              )}
            </div>

            {gamificationLoading ? (
              <p className="text-sm text-neutral-600 text-center py-4">{t('aiReadinessLoading')}</p>
            ) : latestCompletedTasks.length > 0 ? (
              <div className="space-y-3">
                {latestCompletedTasks.map(task => (
                  <div
                    key={task.id}
                    className="flex items-start justify-between p-3 bg-neutral-50 rounded-lg border border-neutral-100"
                  >
                    <div className="flex items-start space-x-3">
                      <span className="text-primary-600 mt-0.5">
                        <CheckCircle className="w-4 h-4" />
                      </span>
                      <div>
                        <h4 className="font-medium text-sm text-neutral-800">{task.title}</h4>
                        <p className="text-xs text-neutral-600">{task.description}</p>
                      </div>
                    </div>
                    <span className="text-xs font-semibold text-green-600">+{task.xpReward} XP</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-neutral-600 text-center py-4">{t('noAiReadinessTasks')}</p>
            )}

            <ButtonLink
              href="https://trainedup.ai/ai-readiness"
              target="_blank"
              rel="noopener noreferrer"
              variant="ghost"
              size="sm"
              fullWidth
              className="mt-4"
            >
              {t('viewAllTasks')}
            </ButtonLink>
          </Card>

          {/* Account Settings */}
          <Card>
            <h3 className="font-montserrat font-semibold text-lg text-neutral-800 mb-4">
              {t('accountSettings')}
            </h3>

            <div className="space-y-3">
              <Button variant="ghost" size="sm" fullWidth className="justify-start">
                {t('changePassword')}
              </Button>
              
              <Button variant="ghost" size="sm" fullWidth className="justify-start">
                {t('privacySettings')}
              </Button>
              
              <Button variant="ghost" size="sm" fullWidth className="justify-start">
                {t('notificationPreferences')}
              </Button>
              
              <Button variant="ghost" size="sm" fullWidth className="justify-start text-red-600 hover:text-red-700 hover:bg-red-50">
                {t('deleteAccount')}
              </Button>
            </div>
          </Card>
        </div>
      </div>
      </div>
    </Layout>
  );
};

